/**
 * Route Configuration
 * การกำหนดค่า Routes
 *
 * Auth pages are outside the App layout (full-screen).
 * All other pages are inside the App layout and require authentication.
 */

import { createBrowserRouter } from 'react-router-dom'
import { App } from './App'
import { HomePage } from '@/features/home'
import { CompanyListPage, CompanyProfilePage } from '@/features/companies'
import { LoginPage } from '@/features/auth/LoginPage'
import { UnauthorizedPage } from '@/features/auth/UnauthorizedPage'
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute'
const ProjectHubPage = () => <div>Project Hub</div> // eslint-disable-line react-refresh/only-export-components
const TBWorkspacePage = () => <div>Trial Balance Workspace</div> // eslint-disable-line react-refresh/only-export-components
const ReportConfigPage = () => <div>Report Configuration</div> // eslint-disable-line react-refresh/only-export-components
const ReportSearchPage = () => <div>Report Search</div> // eslint-disable-line react-refresh/only-export-components
const TemplateManagementPage = () => <div>Template Management</div> // eslint-disable-line react-refresh/only-export-components
const NotFoundPage = () => <div>404 - Page Not Found</div> // eslint-disable-line react-refresh/only-export-components
import { lazy, Suspense } from 'react'

const load = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense
    fallback={
      <div className="text-muted-foreground flex h-full items-center justify-center p-8">
        Loading...
      </div>
    }
  >
    <Component />
  </Suspense>
)

// Auth module pages (lazy imports for code splitting)
const SettingsLayout = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.SettingsLayout }))
)
const ProfilePage = lazy(() => import('@/features/auth').then((m) => ({ default: m.ProfilePage })))
const SecurityPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.SecurityPage }))
)
const AvatarPage = lazy(() => import('@/features/auth').then((m) => ({ default: m.AvatarPage })))
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.ForgotPasswordPage }))
)
const ResetPasswordPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.ResetPasswordPage }))
)
const UserManagementPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.UserManagementPage }))
)
const RoleManagementPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.RoleManagementPage }))
)
const RBACManagementPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.RBACManagementPage }))
)
const ActivityLogPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.ActivityLogPage }))
)

export const router = createBrowserRouter([
  // ─── Public Auth Routes (NO layout, full-screen) ────
  {
    path: '/login',
    element: <LoginPage />,
  },

  // ─── App Shell (layout + auth required) ─────────────
  {
    path: '/',
    element: <App />,
    children: [
      // All routes inside App require authentication
      {
        element: <ProtectedRoute />,
        children: [
          {
            index: true,
            element: <HomePage />,
          },
          // Companies routes — requires companies:read
          {
            element: <ProtectedRoute requiredPermissions={['companies:read']} />,
            children: [
              {
                path: 'companies',
                element: <CompanyListPage />,
              },
              {
                path: 'companies/:id',
                element: <CompanyProfilePage />,
              },
            ],
          },
          {
            path: 'projects',
            element: <ProjectHubPage />,
          },
          {
            path: 'tb/:workspaceId',
            element: <TBWorkspacePage />,
          },
          {
            path: 'settings',
            element: load(SettingsLayout),
            children: [
              {
                index: true,
                element: load(ProfilePage), // Default settings page
              },
              {
                path: 'account/profile',
                element: load(ProfilePage),
              },
              {
                path: 'account/security',
                element: load(SecurityPage),
              },
              {
                path: 'account/avatar',
                element: load(AvatarPage),
              },
              {
                path: 'admin',
                element: <ProtectedRoute requiredPermissions={['users:manage']} />,
                children: [
                  {
                    path: 'users',
                    element: load(UserManagementPage),
                  },
                  {
                    path: 'roles',
                    element: load(RoleManagementPage),
                  },
                  {
                    path: 'rbac',
                    element: load(RBACManagementPage),
                  },
                  {
                    path: 'activity',
                    element: load(ActivityLogPage),
                  },
                ],
              },
            ],
          },
        ],
      },

      // ─── Protected Routes (Auditor+) ────────────────
      {
        element: <ProtectedRoute requiredPermissions={['reports:create']} />,
        children: [
          {
            path: 'reports',
            children: [
              {
                path: 'configure',
                element: <ReportConfigPage />,
              },
              {
                path: 'search',
                element: <ReportSearchPage />,
              },
            ],
          },
        ],
      },

      // ─── Protected Routes (Admin Only) ──────────────
      {
        element: <ProtectedRoute requiredPermissions={['users:manage']} />,
        children: [
          {
            path: 'templates',
            element: <TemplateManagementPage />,
          },
        ],
      },

      // ─── Special Routes ─────────────────────────────
      {
        path: 'unauthorized',
        element: <UnauthorizedPage />,
      },
      {
        path: 'forgot-password',
        element: load(ForgotPasswordPage),
      },
      {
        path: 'reset-password',
        element: load(ResetPasswordPage),
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
])
